import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";

import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { Theme, useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";

import {
  AccessLevelSaveRequest,
  IAccessLevelModel,
  IAccessLevelSaveRequest,
  IOrganizationDetails,
} from "services/KnowfullyAdminClient";
import Field from "components/Field";
import SelectSku from "components/SelectSku";
import useGlobalContext from "hooks/globalData";
import { FORM_ERROR } from "final-form";
import translateError from "utils/translateError";
import { useEffect, useState } from "react";
import { AccessLevelTag } from "constants/TagConstants";
import { accessLevelTagIds } from "pages/OrganizationsPage/accessLevelTag";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
}));

const { Form } = withTypes<IAccessLevelSaveRequest>();

type AccessLevelFormProps = {
  onCancel?: () => void;
  onAccessLevelSaved: () => void;
  organization: IOrganizationDetails;
  accessLevel?: IAccessLevelModel;
  maxSequence?: number;
  submitLabel: string;
};

const AccessLevelForm = ({
  onCancel,
  onAccessLevelSaved,
  organization,
  accessLevel,
  maxSequence,
  submitLabel,
}: AccessLevelFormProps) => {
  const { formatMessage: t } = useIntl();
  const { accessLevelService, skuService } = useGlobalContext();
  const [skuTitle, setSkuTitle] = useState<string | undefined>("");
  // SelectSku searches as soon as it mounts, so it waits for the tag to avoid an unfiltered search.
  const { data: accessLevelTag, isLoading: isLoadingAccessLevelTag } = useQuery(
    ["skuService.getTagByCodeConstant", AccessLevelTag],
    () => skuService.getTagByCodeConstant(AccessLevelTag),
    { retry: false }
  );

  const styles = useStyles(useTheme());
  const init = {
    id: accessLevel?.id,
    organizationId: organization.id,
    skuId: accessLevel?.skuId,
    displayName: accessLevel?.displayName,
    sequence: accessLevel?.sequence ?? maxSequence ?? 0,
  };

  useEffect(() => {
    setSkuTitle(accessLevel?.skuTitle);
  }, [accessLevel]);

  const onSubmit = async (values: IAccessLevelSaveRequest) => {
    try {
      await accessLevelService.saveAccessLevel(
        new AccessLevelSaveRequest(values)
      );
      onAccessLevelSaved();
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError, values }) => (
        <form onSubmit={handleSubmit} noValidate>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Field name="skuId" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={meta.error && meta.submitFailed}
                    >
                      {isLoadingAccessLevelTag ? (
                        <TextField
                          disabled
                          required
                          variant="standard"
                          fullWidth
                          label={t({ id: "skuLabel" })}
                        />
                      ) : (
                        <SelectSku
                          {...input}
                          required
                          organizationId={organization.id}
                          tagIds={accessLevelTagIds(accessLevelTag)}
                          label={t({ id: "skuLabel" })}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                          onSkuChange={(sku) => setSkuTitle(sku?.title ?? "")}
                        />
                      )}
                      {meta.error && meta.submitFailed && (
                        <FormHelperText>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Field>

              <Grid item xs={12}>
                <TextField
                  disabled={true}
                  variant="standard"
                  fullWidth
                  label={t({ id: "skuTitleColumnLabel" })}
                  value={skuTitle}
                />
              </Grid>

              <Field name="displayName" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      required
                      variant="standard"
                      fullWidth
                      label={t({ id: "displayNameLabel" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={submitting || !values.skuId}
            >
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default AccessLevelForm;
